import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { makeStyles } from '@material-ui/core/styles';

import {
  dateKey,
  formatMoment,
  MAX_OCCURRENCES,
  REPEATS,
  slotTime,
  timeSlots,
  WEEKDAYS,
} from '../schedule';
import { colors } from '../theme';
import FieldLabel from './FieldLabel';
import TimeField from './TimeField';
import { kyivNow } from '../kyivTime';

const TIME_MODES = [
  { value: 'same', label: 'Одно время для всех' },
  { value: 'each', label: 'Своё время для каждой' },
];

const useStyles = makeStyles((theme) => ({
  block: { marginTop: theme.spacing(2) },
  calendar: {
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    maxWidth: 340,
  },
  calendarHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  month: { fontWeight: 600, fontSize: '14px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 },
  weekday: { textAlign: 'center', color: colors.textDim, fontSize: '11px', padding: '4px 0' },
  day: {
    border: 'none',
    borderRadius: 8,
    height: 36,
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: colors.text,
    fontSize: '13px',
    '&:hover': { backgroundColor: colors.accentSoft },
    '&:disabled': { color: colors.textFaint, cursor: 'default', backgroundColor: 'transparent' },
  },
  daySelected: {
    backgroundColor: colors.accent,
    color: '#fff',
    fontWeight: 700,
    '&:hover': { backgroundColor: colors.accentHover },
  },
  dayToday: { boxShadow: `inset 0 0 0 1px ${colors.accent}` },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: '6px 14px',
    backgroundColor: colors.panel,
    color: colors.text,
    cursor: 'pointer',
    fontSize: '13px',
    '&:hover': { borderColor: colors.accent },
  },
  chipOn: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
    color: '#fff',
    fontWeight: 600,
    '&:hover': { borderColor: colors.accent, backgroundColor: colors.accentHover },
  },
  monthDays: { display: 'grid', gridTemplateColumns: 'repeat(7, 44px)', gap: 6 },
  monthDay: { padding: '6px 0', textAlign: 'center', borderRadius: 8 },
  preview: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 10,
    backgroundColor: colors.panelAlt,
  },
  previewTitle: { fontWeight: 700, fontSize: '14px', marginBottom: 6 },
  previewList: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  previewItem: {
    backgroundColor: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: 14,
    padding: '2px 10px',
    fontSize: '12px',
  },
  slots: { display: 'flex', flexDirection: 'column', gap: 8 },
  slot: { display: 'flex', alignItems: 'center', gap: 12 },
  slotLabel: { width: 110, fontSize: '13px', color: colors.text },
  note: { color: colors.textDim, fontSize: '12px', marginTop: 8, lineHeight: 1.45 },
  problem: { color: '#d32f2f', fontSize: '13px', marginTop: 8 },
}));

// «октябрь 2026 г.» -> «Октябрь 2026»
const monthLabel = (year, month) => {
  const text = new Date(year, month, 1)
    .toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
    .replace(/\s*г\.?$/, '');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Календарь с выбором нескольких дней: нажатие включает и выключает день.
function MultiCalendar({ classes, selected, onToggle }) {
  const today = kyivNow();
  const todayKey = dateKey(today);
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });

  const first = new Date(cursor.year, cursor.month, 1);
  const length = new Date(cursor.year, cursor.month + 1, 0).getDate();
  const blanks = (first.getDay() + 6) % 7; // неделя начинается с понедельника

  const move = (delta) => {
    const next = new Date(cursor.year, cursor.month + delta, 1);
    setCursor({ year: next.getFullYear(), month: next.getMonth() });
  };

  const cells = [];
  for (let i = 0; i < blanks; i += 1) cells.push(<span key={`b${i}`} />);
  for (let day = 1; day <= length; day += 1) {
    const key = dateKey(new Date(cursor.year, cursor.month, day));
    const isSelected = selected.includes(key);
    cells.push(
      <button
        key={key}
        type="button"
        className={`${classes.day} ${isSelected ? classes.daySelected : ''} ${
          key === todayKey ? classes.dayToday : ''
        }`}
        disabled={key < todayKey}
        aria-pressed={isSelected}
        onClick={() => onToggle(key)}
      >
        {day}
      </button>
    );
  }

  return (
    <div className={classes.calendar}>
      <div className={classes.calendarHead}>
        <IconButton size="small" aria-label="Предыдущий месяц" onClick={() => move(-1)}>
          <ChevronLeftIcon />
        </IconButton>
        <span className={classes.month}>{monthLabel(cursor.year, cursor.month)}</span>
        <IconButton size="small" aria-label="Следующий месяц" onClick={() => move(1)}>
          <ChevronRightIcon />
        </IconButton>
      </div>
      <div className={classes.grid}>
        {WEEKDAYS.map((day) => (
          <span key={day.id} className={classes.weekday}>
            {day.short}
          </span>
        ))}
        {cells}
      </div>
    </div>
  );
}

const toggle = (list, value) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

// Расписание рассылки: как повторять, в какие дни и во сколько, и что из этого выйдет.
// rule — правило (см. schedule.js), result — то, что из него получилось.
function ScheduleEditor({ rule, onChange, result, error }) {
  const classes = useStyles();
  const set = (patch) => onChange({ ...rule, ...patch });
  const slots = timeSlots(rule);

  const period = (
    <div className={classes.block} style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
      <FieldLabel label="С какого числа">
        <TextField
          type="date"
          variant="outlined"
          value={rule.from}
          onChange={(event) => set({ from: event.target.value })}
        />
      </FieldLabel>
      <FieldLabel label="По какое число включительно">
        <TextField
          type="date"
          variant="outlined"
          value={rule.to}
          onChange={(event) => set({ to: event.target.value })}
        />
      </FieldLabel>
    </div>
  );

  const shown = result.list.slice(0, 10);

  return (
    <div>
      <FieldLabel label="Как отправлять">
        <TextField
          select
          fullWidth
          variant="outlined"
          value={rule.repeat}
          // Своё время хранится под ключами режима (дата, день недели, число), в другом режиме они значат другое.
          onChange={(event) => set({ repeat: event.target.value, times: {} })}
        >
          {REPEATS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FieldLabel>

      {rule.repeat === 'once' && (
        <div className={classes.block}>
          <FieldLabel label="Дата отправки">
            <TextField
              fullWidth
              type="date"
              variant="outlined"
              value={rule.date}
              onChange={(event) => set({ date: event.target.value })}
            />
          </FieldLabel>
        </div>
      )}

      {rule.repeat === 'dates' && (
        <div className={classes.block}>
          <FieldLabel
            label="Даты отправки"
            hint="Нажимайте на дни, чтобы выбрать несколько. Повторное нажатие снимает выбор"
          >
            <MultiCalendar
              classes={classes}
              selected={rule.dates}
              onToggle={(key) => set({ dates: toggle(rule.dates, key).sort() })}
            />
          </FieldLabel>
          {rule.dates.length > 0 && (
            <Button size="small" onClick={() => set({ dates: [] })} style={{ marginTop: 8 }}>
              Снять все даты
            </Button>
          )}
        </div>
      )}

      {rule.repeat === 'weekly' && (
        <div className={classes.block}>
          <FieldLabel label="Дни недели">
            <div className={classes.chips}>
              {WEEKDAYS.map((day) => {
                const on = rule.weekdays.includes(day.id);
                return (
                  <button
                    key={day.id}
                    type="button"
                    aria-pressed={on}
                    className={`${classes.chip} ${on ? classes.chipOn : ''}`}
                    onClick={() => set({ weekdays: toggle(rule.weekdays, day.id) })}
                  >
                    {day.short}
                  </button>
                );
              })}
            </div>
          </FieldLabel>
          {period}
        </div>
      )}

      {rule.repeat === 'monthly' && (
        <div className={classes.block}>
          <FieldLabel label="Числа месяца">
            <div className={classes.monthDays}>
              {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => {
                const on = rule.monthDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    className={`${classes.chip} ${classes.monthDay} ${on ? classes.chipOn : ''}`}
                    onClick={() => set({ monthDays: toggle(rule.monthDays, day) })}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <div className={classes.chips} style={{ marginTop: 8 }}>
              <button
                type="button"
                aria-pressed={rule.monthDays.includes('last')}
                className={`${classes.chip} ${rule.monthDays.includes('last') ? classes.chipOn : ''}`}
                onClick={() => set({ monthDays: toggle(rule.monthDays, 'last') })}
              >
                Последнее число месяца
              </button>
            </div>
          </FieldLabel>
          {period}
        </div>
      )}

      <div className={classes.block}>
        {rule.repeat !== 'once' && slots.length > 0 && (
          <div className={classes.chips} style={{ marginBottom: 12 }}>
            {TIME_MODES.map((mode) => (
              <button
                key={mode.value}
                type="button"
                aria-pressed={rule.timeMode === mode.value}
                className={`${classes.chip} ${rule.timeMode === mode.value ? classes.chipOn : ''}`}
                onClick={() => set({ timeMode: mode.value })}
              >
                {mode.label}
              </button>
            ))}
          </div>
        )}

        {(rule.repeat === 'once' || rule.timeMode !== 'each' || slots.length === 0) && (
          <FieldLabel label="Время" hint="По киевскому времени">
            <TimeField value={rule.time} onChange={(time) => set({ time })} />
          </FieldLabel>
        )}

        {rule.repeat !== 'once' && rule.timeMode === 'each' && slots.length > 0 && (
          <FieldLabel label="Время каждой отправки" hint="По киевскому времени">
            <div className={classes.slots}>
              {slots.map((slot) => (
                <div key={slot.key} className={classes.slot}>
                  <span className={classes.slotLabel}>{slot.label}</span>
                  <TimeField
                    size="small"
                    aria-label={`Время: ${slot.label}`}
                    value={slotTime(rule, slot.key)}
                    onChange={(time) => set({ times: { ...rule.times, [slot.key]: time } })}
                  />
                </div>
              ))}
            </div>
            <div className={classes.slot} style={{ marginTop: 10 }}>
              <span className={classes.slotLabel}>Всем сразу</span>
              <TimeField
                size="small"
                aria-label="Время: всем сразу"
                value={rule.time}
                onChange={(time) =>
                  set({
                    time,
                    times: slots.reduce((all, slot) => ({ ...all, [slot.key]: time }), {}),
                  })
                }
              />
            </div>
          </FieldLabel>
        )}
      </div>

      <div className={classes.preview}>
        {result.list.length > 0 ? (
          <>
            <div className={classes.previewTitle}>
              {result.list.length === 1
                ? 'Будет 1 отправка'
                : `Будет отправок: ${result.list.length}`}
            </div>
            <div className={classes.previewList}>
              {shown.map((moment) => (
                <span key={moment.getTime()} className={classes.previewItem}>
                  {formatMoment(moment)}
                </span>
              ))}
              {result.list.length > shown.length && (
                <span className={classes.previewItem}>и ещё {result.list.length - shown.length}</span>
              )}
            </div>
          </>
        ) : (
          <div className={classes.previewTitle}>Пока нет отправок</div>
        )}
        {result.past > 0 && (
          <div className={classes.note}>
            Уже прошедшие даты пропущены: {result.past}.
          </div>
        )}
        {result.tooFar > 0 && (
          <div className={classes.note}>
            Дальше чем через год планировать нельзя, пропущено: {result.tooFar}.
          </div>
        )}
        {rule.repeat === 'monthly' && rule.monthDays.some((day) => typeof day === 'number' && day > 28) && (
          <div className={classes.note}>
            В месяцах, где нет такого числа (29, 30, 31), отправка в этом месяце пропускается.
            Чтобы писать в конце каждого месяца, выберите «Последнее число месяца».
          </div>
        )}
        {result.list.length > 1 && (
          <div className={classes.note}>
            Все отправки серии идут одному и тому же списку с одними и теми же значениями шаблона. Перед
            каждой сервер заново проверит согласия и дневной лимит номера. Не больше {MAX_OCCURRENCES}{' '}
            отправок за раз.
          </div>
        )}
        {(error || result.problem) && <div className={classes.problem}>{error || result.problem}</div>}
      </div>
    </div>
  );
}

export default ScheduleEditor;
